import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/padel-club-real.jpg";

export const sportClubs: Persona = {
  slug: "sport-clubs",
  profession: { hu: "sportklub-vezető", en: "sport club director" },
  professionPlural: {
    hu: "sportklubok és egyesületek",
    en: "sport clubs and associations",
  },
  professionGenitive: {
    hu: "sportklub-vezetőként",
    en: "as a sport club director",
  },
  heroH1: {
    hu: "Csapat, edzők, tagok: egy rendszerben, egy ár.",
    en: "Team, coaches, members: one system, one price.",
  },
  heroSub: {
    hu: "AI weboldal, edzés-foglalás, tagdíj-kezelés és csapat-adminisztráció: egy platformon sportklubokhoz és egyesületekhez.",
    en: "AI website, training bookings, membership-fee management and team admin: one platform for sport clubs and associations. ",
  },
  heroImage,
  heroImageAlt: "Sport club team training together",
  chatExamplePrompt: {
    hu: "Hozz létre egy 'Téli kihívás' programot, 8 hét, 45 000 Ft",
    en: "Create a 'Winter challenge' program, 8 weeks, 45,000 HUF",
  },
  chatExampleResponse: {
    hu: "Létrehoztam: Téli kihívás 8 hetes program, heti 3 edzés + havi mérés, 45 000 Ft. Élesítve, regisztrációs oldal kész.",
    en: "Created: Winter challenge 8-week program, 3 sessions/week + monthly assessment, 45,000 HUF. Live with signup page ready.",
  },
  accentColor: "blue",

  defaultMode: "team",
  teamSize: { min: 3, max: 30 },
  soloLabel: { hu: "Kisebb klub (3:8 edző)", en: "Smaller club (3:8 coaches)" },
  teamLabel: {
    hu: "Nagy klub vagy egyesület",
    en: "Large club or association",
  },

  soloDayInLife: [
    {
      time: "07:00",
      what: {
        hu: "Klub dashboard: 5 edződ 18 edzést tart ma. Mindenki naptára átláthatóan.",
        en: "Club dashboard: your 5 coaches run 18 sessions today. All calendars at a glance.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "Új tag jön: az AI automatikusan a megfelelő szakághoz (gyerek-csoport, felnőtt-amatőr, versenyző) osztja.",
        en: "New member joins: AI auto-routes them to the right group (kids, adult amateur, competitor).",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Edzői megbeszélés. Az AI riportot küld a hét teljesítményéről: tagdíj-befizetések, részvétel, lemorzsolódás.",
        en: "Coach meeting. AI report on the week: fees, attendance, churn.",
      },
    },
    {
      time: "17:00",
      what: {
        hu: "Egy edző beteg. Az AI áthelyezi 2 csoportját másik edzőhöz, szülőket értesít.",
        en: "A coach is sick. AI moves their 2 groups to another coach, notifies parents.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Esti edzés. Mind az 18 résztvevő automatikusan check-inelt.",
        en: "Evening session. All 18 attendees auto-checked-in.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Napzárás: edzés-statisztika, új tag-jelentkezők, tagdíj-bevétel.",
        en: "End of day: training stats, new member signups, fee revenue.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "07:00",
      what: {
        hu: "Klub dashboard: 12 edződ 45 edzést tart ma, 4 helyszínen. Mindenki saját nézettel.",
        en: "Club dashboard: 12 coaches run 45 sessions today at 4 locations. Each with their own view.",
      },
    },
    {
      time: "09:30",
      what: {
        hu: "Új csapat indítása chatben ('U14 fiú leigazolt'). Az AI létrehozza a csapat-oldalt, edző-hozzárendelést, edzés-naptárat.",
        en: "Launch a new team in chat ('U14 boys signed'). AI creates the team page, coach assignment and training calendar.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Két edző beteg. Az AI átszervezi 6 csoport edzését, szülőket és játékosokat értesít.",
        en: "Two coaches are sick. AI re-organizes 6 group sessions, notifies parents and players.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Manager-riport: ki mennyit edzett, melyik csapat fizetett tagdíjat, melyik lemaradásban van.",
        en: "Manager report: who coached how much, which team paid, which is behind.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Verseny-regisztráció telik. Az AI várólistára teszi a túljelentkezőket, busz-szervezést javasol.",
        en: "Competition signups fill up. AI puts overflow on the waitlist, suggests bus arrangements.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Hónap vége: bevétel csapatonként, edzői bér-elszámolás, tagdíj-felszólítók automatikusan kiküldve.",
        en: "Month-end: revenue per team, coach payroll, fee reminders sent automatically.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: { hu: "Egy hely a klubnak", en: "One place for the club" },
      body: {
        hu: "Edzés-naptár, tag-lista, tagdíj: egy fiókban, mobilról is kezelhető.",
        en: "Training calendar, member list, fees: one account, mobile-friendly.",
      },
    },
    {
      icon: "Bot",
      title: {
        hu: "Az AI a klub-titkár helyett",
        en: "AI replaces the club secretary",
      },
      body: {
        hu: "Új tag-felvétel, tagdíj-figyelés, versenyzői nevezések: automatikusan.",
        en: "New member intake, fee tracking, competition entries: all automated.",
      },
    },
    {
      icon: "Users",
      title: { hu: "Szülő-kommunikáció", en: "Parent communication" },
      body: {
        hu: "Gyerek-csoportoknál a szülők saját profilon látják a gyermek edzéseit, fizetést, eseményeket.",
        en: "For kids' groups, parents see their child's sessions, payments and events in their own profile.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan edző és csapat, egy ár",
        en: "Unlimited coaches and teams, one price",
      },
      body: {
        hu: "3 vagy 30 edző, 5 vagy 50 csapat: ugyanaz a havidíj. Korlátlan tag.",
        en: "3 or 30 coaches, 5 or 50 teams: same monthly fee. Unlimited members.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Jogosultság-kezelés", en: "Role-based access" },
      body: {
        hu: "Klubelnök, manager, edző, recepciós: mindenki csak azt látja, amihez joga van.",
        en: "President, manager, coach, receptionist: everyone sees only what they're allowed to.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI csapat-elosztás", en: "AI team assignment" },
      body: {
        hu: "Új gyerek jön: az AI a korcsoport, képesség, szabad slot alapján a megfelelő csapathoz osztja.",
        en: "New kid joins: AI assigns to the right team by age, ability and free slots.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Verseny- és bérelszámolás",
        en: "Competition and payroll settlement",
      },
      body: {
        hu: "Edzői bér, verseny-bevétel, busz-költség, szövetségi tagdíj: minden egy helyen, exportálva.",
        en: "Coach pay, competition revenue, bus costs, federation fees: all in one place, exportable.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: {
        hu: "Csoportos edzések és kurzusok",
        en: "Group sessions and courses",
      },
      body: {
        hu: "Létszámkorlát, várólista, bérletvonás, csere: minden automatikus.",
        en: "Capacity limits, waitlist, pass deduction, swaps: all automated.",
      },
    },
    {
      icon: "CreditCard",
      title: { hu: "Bérletek és tagdíjak", en: "Passes and membership fees" },
      body: {
        hu: "Havi tagdíj, 10 alkalmas bérlet, éves tagság: automatikus levonás foglaláskor, lejárat előtt emlékeztető.",
        en: "Monthly fees, 10-class passes, annual memberships: auto-deducted at booking, reminder before expiry.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "Visszahívó kampányok", en: "Win-back campaigns" },
      body: {
        hu: "Ha egy tag kiesik a ritmusból, egy kattintással elindítasz egy automata emlékeztető e-mailt.",
        en: "If a member drops off, an automatic reminder email: launch it in one click.",
      },
    },
  ],

  objections: [
    {
      question: {
        hu: "30 edzőnk és 50 csapatunk van: bonyolult lesz?",
        en: "We have 30 coaches and 50 teams: will it be complicated?",
      },
      answer: {
        hu: "Pont a nagy klubokra optimalizáltunk. Minden edző saját nézettel, minden csapat saját aldomainnel, te klubelnökként a centralizált dashboardon mindent látsz. Korlátlan edző, korlátlan csapat, korlátlan tag, NEM fizetsz többet. 3 vagy 30 fő, ugyanaz a 10 000 Ft + ÁFA / hó.",
        en: "We optimized for large clubs. Each coach has their own view, each team a subdomain, you as president see everything on the central dashboard. Unlimited coaches, teams and members, you do NOT pay more. 3 or 30, same 10,000 HUF + VAT / month.",
      },
    },
    {
      question: {
        hu: "A szülő-kommunikáció kaotikus (WhatsApp + email + telefon)",
        en: "Parent communication is chaos (WhatsApp + email + phone)",
      },
      answer: {
        hu: "Egységes szülő-portál: a szülő saját profilon látja a gyermek edzéseit, tagdíj-fizetéseket, eseményeket. Push-üzenet, email, mind a Bespire-en keresztül. Kevesebb káosz, kevesebb félreértés.",
        en: "Unified parent portal: each parent has a profile with their child's sessions, payments and events. Push notifications and emails all via Bespire. Less chaos, fewer misunderstandings.",
      },
    },
    {
      question: {
        hu: "Tagdíj-felszólítás unalmas és személytelen",
        en: "Fee reminders are boring and impersonal",
      },
      answer: {
        hu: "Az AI automatikusan barátságos hangnemben emlékeztet 3 nappal a lejárat előtt, majd 1 héttel utána. Ha nem fizet, az edzőnek riport megy. A kintlévőség látványosan csökken.",
        en: "AI auto-reminds in a friendly tone 3 days before due, then 1 week after. If unpaid, the coach gets a report. Overdue dramatically drops.",
      },
    },
    {
      question: {
        hu: "Versenyek, busz-szervezés, szövetségi nevezés...",
        en: "Competitions, bus arrangements, federation entries...",
      },
      answer: {
        hu: "Egy oldalon: verseny-nevezés (létszám-korlát, várólista), busz-szervezés (utazó lista, ülés-rend), szövetségi adatok export (MLSZ, MJSZ kompatibilis). Csökken a manuális Excel-munka.",
        en: "All in one page: competition entries (capacity, waitlist), bus arrangements (passenger list, seating), federation data export (MLSZ, MJSZ compatible). Manual Excel work drops.",
      },
    },
  ],

  monetization: [
    {
      icon: "Calendar",
      title: { hu: "Edzések és csoportok", en: "Sessions and groups" },
      examples: [
        { hu: "Gyerek-csoport (heti 2)", en: "Kids' group (2×/week)" },
        { hu: "Felnőtt-amatőr csoport", en: "Adult amateur group" },
        { hu: "Versenyző csoport", en: "Competitor group" },
      ],
      priceHint: {
        hu: "Példa: 200 tag × 12 000 Ft tagdíj = 2 400 000 Ft / hó",
        en: "Example: 200 members × 12,000 HUF fee = 2,400,000 HUF / month",
      },
    },
    {
      icon: "Dumbbell",
      title: { hu: "Speciális programok", en: "Special programs" },
      examples: [
        { hu: "Téli kihívás (8 hét)", en: "Winter challenge (8 weeks)" },
        { hu: "Nyári tábor (1 hét)", en: "Summer camp (1 week)" },
        { hu: "Versenyre felkészítő tábor", en: "Competition prep camp" },
      ],
      priceHint: {
        hu: "Példa: 1 nyári tábor × 40 fő × 65 000 Ft = 2 600 000 Ft",
        en: "Example: 1 summer camp × 40 people × 65,000 HUF = 2,600,000 HUF",
      },
    },
    {
      icon: "Heart",
      title: { hu: "Egyéni szolgáltatások", en: "Individual services" },
      examples: [
        { hu: "Személyi edzés", en: "Personal training" },
        { hu: "Technikai konzultáció", en: "Technical consultation" },
        { hu: "Sport-mentális coaching", en: "Sports mental coaching" },
      ],
      priceHint: {
        hu: "Példa: 60 alkalom × 14 000 Ft = 840 000 Ft / hó",
        en: "Example: 60 sessions × 14,000 HUF = 840,000 HUF / month",
      },
    },
    {
      icon: "Package",
      title: { hu: "Felszerelés és kiegészítő", en: "Gear and add-ons" },
      examples: [
        { hu: "Klub-mez és szerelés", en: "Club kit and gear" },
        { hu: "Speciális eszközök", en: "Special equipment" },
        { hu: "Sport-táplálék-kiegészítők", en: "Sports supplements" },
      ],
      priceHint: {
        hu: "Példa: ~250 000 Ft / hó (mez + esemény-belépő)",
        en: "Example: ~250,000 HUF / month (kits + event tickets)",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Tagság és bérletek", en: "Membership and passes" },
      examples: [
        { hu: "Havi tagdíj", en: "Monthly fee" },
        { hu: "Éves klub-tagság", en: "Annual club membership" },
        { hu: "VIP versenyző-tagság", en: "VIP competitor membership" },
      ],
      priceHint: {
        hu: "Klub-szint: 15 edző × 250 tag × 12 000 Ft = 3 000 000 Ft / hó potenciál (nagy klubnál akár 8M+)",
        en: "Club level: 15 coaches × 250 members × 12,000 HUF = 3,000,000 HUF / month potential (large clubs 8M+)",
      },
    },
  ],

  results: [
    {
      value: "+38%",
      label: { hu: "tagsági megújulás", en: "membership renewal" },
    },
    {
      value: "-65%",
      label: { hu: "edző-koordinációs idő", en: "coach coordination time" },
    },
    {
      value: "4.8★",
      label: { hu: "tag-elégedettség", en: "member satisfaction" },
    },
    {
      value: "1 ár",
      label: { hu: "1:30 edző között", en: "for 1:30 coaches" },
    },
  ],
  testimonial: {
    quote: {
      hu: "8 edző, 240 aktív tag, 4 pálya: egy rendszerben. A bér-számítás és pálya-foglalás automata, az időm felszabadult a sportszakmai munkára.",
      en: "8 coaches, 240 active members, 4 courts: one system. Payroll and court bookings are automated, my time is free for the actual sport work.",
    },
    name: "Király Péter",
    role: { hu: "Sportklub-elnök", en: "Sport club president" },
    city: { hu: "Miskolc", en: "Miskolc" },
    avatarInitials: "KP",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a klubod struktúráját",
        en: "Tell us your club structure in chat",
      },
      body: {
        hu: "Edzők, csoportok, pályák, tagsági szintek: az AI feltérképezi.",
        en: "Coaches, groups, courts, membership tiers: AI maps them out.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti a klub-oldalt",
        en: "AI builds your club site",
      },
      body: {
        hu: "Tagok, edzők, foglalás, jutalék-elszámolás: minden élesítve.",
        en: "Members, coaches, booking, commission: everything live.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Importáljuk a tagság-adatokat",
        en: "We import membership data",
      },
      body: {
        hu: "Régi rendszerből (Mindbody, egyedi), CSV-vel.",
        en: "From the old system (Mindbody, custom) via CSV.",
      },
    },
    {
      step: 4,
      title: {
        hu: "Élő, tagok online foglalnak",
        en: "Live, members book online",
      },
      body: {
        hu: "Saját portál, bérlet-vonás, várólista.",
        en: "Own portal, pass deduction, waitlist.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Stripe",
    "SimplePay",
    "Apple Pay",
    "Google Pay",
    "Meta",
    "WhatsApp Business",
    "Mailchimp",
  ],
  breadcrumbLabel: {
    hu: "Sportklubok és egyesületek",
    en: "Sport clubs & associations",
  },
  similarPersonas: [
    "fitness-personal-trainers",
    "yoga-pilates-instructors",
    "physiotherapists",
  ],
};
